/**
 * Where a study's output actually lives.
 *
 * `RecentCase` is the only list of every study the app knows, and it carries
 * no `outputRoot` — but it does carry `caseJsonPath`, and `project.rs` writes
 * the manifest at exactly `<outputRoot>/<subjectId>/case.json` and nowhere
 * else. So the root is recoverable, and recovering it is what turns a list of
 * studies into a view of where your results went.
 *
 * It is an inference off a naming convention, which is the kind of string
 * surgery that breaks quietly, so it is a named function with tests rather
 * than a regex inline in a component — and it returns `null` rather than a
 * plausible wrong answer when the path is not that shape.
 */

/** The last path separator, tolerating Windows separators in a saved case. */
function splitLast(path: string): [string, string] | null {
  const i = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  if (i <= 0) return null;
  return [path.slice(0, i), path.slice(i + 1)];
}

/** `<outputRoot>/<subjectId>/case.json` → `<outputRoot>/<subjectId>`. */
export function caseDirOf(caseJsonPath: string): string | null {
  const split = splitLast(caseJsonPath);
  if (!split) return null;
  const [dir, file] = split;
  return file.toLowerCase().endsWith(".json") ? dir : null;
}

/** `<outputRoot>/<subjectId>/case.json` → `<outputRoot>`. */
export function outputRootOf(caseJsonPath: string): string | null {
  const caseDir = caseDirOf(caseJsonPath);
  if (!caseDir) return null;
  const split = splitLast(caseDir);
  return split ? split[0] : null;
}

export interface OutputGroup<T> {
  /** The folder every study below was written into, or null if not derivable. */
  root: string | null;
  cases: T[];
}

/**
 * Studies bucketed by the folder they were written into, roots in first-seen
 * order so the list does not reshuffle when one study's mtime changes.
 * Undecidable paths collect in a single trailing group rather than each
 * becoming a root of its own.
 */
export function groupByOutputRoot<T extends { caseJsonPath: string }>(
  cases: T[],
): OutputGroup<T>[] {
  const order: (string | null)[] = [];
  const buckets = new Map<string, T[]>();
  const key = (root: string | null) => (root == null ? "\u0000unknown" : root);

  for (const c of cases) {
    const root = outputRootOf(c.caseJsonPath);
    const k = key(root);
    if (!buckets.has(k)) {
      buckets.set(k, []);
      order.push(root);
    }
    buckets.get(k)!.push(c);
  }

  const groups = order.map((root) => ({ root, cases: buckets.get(key(root))! }));
  // Unplaceable studies last: they are the exception, and leading with them
  // would suggest the list is mostly broken.
  return [...groups.filter((g) => g.root != null), ...groups.filter((g) => g.root == null)];
}
